<script setup lang="ts">
/**
 * Montant dans la devise de l'utilisateur, toujours en chiffres tabulaires. La devise est plus discrète que le nombre.
 * `signed` affiche « + » ou « − » (revenus et dépenses dans un historique).
 */
import { computed } from 'vue'

import { fr } from '@/copy/fr'
import { activeCurrency, currencySpoken, currencySymbol, formatMoney } from '@/domain'

import { amountsHidden } from '../privacy'

const props = withDefaults(
  defineProps<{
    value: number
    size?: 'entry' | 'hero' | 'title' | 'section' | 'body' | 'label'
    signed?: 'income' | 'expense' | 'none'
    tone?: 'default' | 'muted' | 'positive' | 'danger'
  }>(),
  { size: 'body', signed: 'none', tone: 'default' },
)

const SIZES = {
  entry: 'text-entry',
  hero: 'text-hero',
  title: 'text-title',
  section: 'text-section',
  body: 'text-body',
  label: 'text-label',
} as const

const TONES = {
  default: 'text-ink',
  muted: 'text-ink-muted',
  positive: 'text-positive',
  danger: 'text-danger',
} as const

const PLUS = '+'
const MINUS = String.fromCharCode(0x2212)

const digits = computed(() => {
  const formatted = formatMoney(Math.abs(props.value), { withCurrency: false })
  if (props.signed === 'income') return `${PLUS}${formatted}`
  if (props.signed === 'expense') return `${MINUS}${formatted}`
  return props.value < 0 ? `${MINUS}${formatted}` : formatted
})

const MASK = String.fromCharCode(0x2022).repeat(5)

const symbol = computed(() => currencySymbol(activeCurrency()))

/** Phrase lue par les lecteurs d'écran : « moins 1 500 francs CFA ». */
const spoken = computed(() => {
  if (amountsHidden.value) return fr.ui.amountHidden
  const amount = formatMoney(Math.abs(props.value), { withCurrency: false })
  const negative = props.signed === 'expense' || (props.signed === 'none' && props.value < 0)
  return `${negative ? 'moins ' : ''}${amount} ${currencySpoken(activeCurrency())}`
})

/** La devise reste en retrait : un cran de taille sous le nombre. */
const CURRENCY = {
  entry: 'text-section font-medium',
  hero: 'text-section font-medium',
  title: 'text-body font-medium',
  section: 'text-label',
  body: 'text-caption',
  label: 'text-caption',
} as const
</script>

<template>
  <span
    :class="['inline-flex items-baseline gap-1 tabular-nums', SIZES[size], TONES[tone]]"
    :aria-label="spoken"
  >
    <span aria-hidden="true">{{ amountsHidden ? MASK : digits }}</span>
    <span aria-hidden="true" :class="[CURRENCY[size], 'text-ink-muted']">{{ symbol }}</span>
  </span>
</template>
